import React, { Component } from 'react';
import PropTypes from 'prop-types';

import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import TextField from '@material-ui/core/TextField';
import HtmlTooltip from './HtmlTooltip';

import styled from 'styled-components';

import { asField } from 'informed';
import { get, toNumber } from 'lodash-es';

import NumberFormat from './NumericNumberFormatComponent';

const StyledFormControl = styled(FormControl)`
  width: 100%;
`;

// Hack because InputProps.inputComponent can't take custom props
// eslint-disable-next-line react/display-name
const NumberFormatWithProps = customProps => props => <NumberFormat {...customProps} {...props} />;

class NumberFieldComponent extends Component {
  constructor(props) {
    super(props);
    this.inputRef = React.createRef();
  }

  // workaround to prevent an issue of react-number-format with decimalSeparator: ','
  formatValue = val => {
    if (this.getNavigatorLanguage().startsWith('fr')) {
      return toNumber(`${val}`.replace(',', '.'));
    }
    return val;
  };

  handleChange = e => {
    const { fieldApi, onChange, technicalKind } = this.props;
    let value = get(e, 'target.value');

    if (technicalKind === 'numeric' && value % 1) {
      value = `${value}`;
    }

    if (fieldApi.setValue) fieldApi.setValue(value);

    if (onChange) {
      onChange(value);
    }
  };

  getNavigatorLanguage = () => {
    const { lang } = this.props;
    if (lang) return lang;

    if (navigator.languages && navigator.languages.length) {
      return navigator.languages[0];
    }

    return navigator.userLanguage || navigator.language || navigator.browserLanguage || 'fr';
  };

  getPropsLocale = () => {
    const { technicalKind } = this.props;
    const lang = this.getNavigatorLanguage();
    const customProps = {};

    if (technicalKind === 'integer') {
      customProps.decimalScale = 0;
    }

    if (lang.startsWith('en')) {
      return { ...customProps, thousandSeparator: true, decimalSeparator: '.' };
    }

    if (lang.startsWith('hi') || lang.startsWith('ne') || lang.startsWith('bn') || lang.startsWith('ur')) {
      // Inde (Hindi), Népal (NE), Bangladesh (BN), Pakistan (Urdu)
      return { ...customProps, thousandSeparator: true, thousandsGroupStyle: 'lakh', decimalSeparator: '.' };
    }

    return { ...customProps, thousandSeparator: ' ' };
  };

  // eslint-disable-next-line react/sort-comp
  numberFormatSetter = NumberFormatWithProps(this.getPropsLocale());

  render() {
    const { fieldApi, fieldState, ...props } = this.props;
    const { value, error } = fieldState;
    const { setTouched } = fieldApi;
    const {
      classes,
      onBlur,
      field,
      defaultValue,
      multiple,
      shrink,
      label = '',
      id = String(Math.floor(Math.random() * 1e8)),
      helperText = '',
      forwardedRef,
      tooltip = '',
      ...rest // eslint-disable-line react/jsx-props-no-spreading
    } = props;

    return (
      <>
        {tooltip && (
          <HtmlTooltip title={tooltip}>
            <span className="helper">?</span>
          </HtmlTooltip>
        )}
        <StyledFormControl error={!!error}>
          <TextField
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...rest}
            name={field}
            label={label}
            defaultValue={defaultValue}
            ref={this.inputRef}
            value={value !== null && value !== undefined ? this.formatValue(value) : ''}
            InputLabelProps={{ shrink }}
            onChange={this.handleChange}
            onBlur={e => {
              setTouched();
              if (onBlur) {
                onBlur(e);
              }
            }}
            // material-ui props
            InputProps={{
              inputComponent: this.numberFormatSetter,
              id,
            }}
          />
          <FormHelperText>{error || helperText}</FormHelperText>
        </StyledFormControl>
      </>
    );
  }
}

NumberFieldComponent.propTypes = {
  lang: PropTypes.string,
  fieldApi: PropTypes.shape({
    setTouched: PropTypes.func,
    setValue: PropTypes.func,
  }).isRequired,
  fieldState: PropTypes.shape({
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    error: PropTypes.string,
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  technicalKind: PropTypes.string.isRequired,
  tooltip: PropTypes.string,
};

NumberFieldComponent.defaultProps = { lang: undefined };

export default asField(NumberFieldComponent);
